import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
  HttpResponse,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@mzic/mzic-environments';
import { Observable, catchError, map, tap, throwError } from 'rxjs';
import {
  COLLECTION_IDEMPOTENCY_OPERATION,
  CommitTargetFileSetIdempotentOperation,
} from '../idempotency/collection-request-idempotency';
import { CollectionRequestIdempotencyService } from '../idempotency/collection-request-idempotency.service';
import {
  CollectionRevisionCurrentDto,
  CollectionIntegrationAuthorization,
  CollectionRevisionDto,
  CollectionRevisionFileSetCommitResponse,
  CollectionRevisionResponse,
  CollectionRevisionTargetFileSetResponse,
  CollectionRevisionValidationResponse,
  CollectionWorkflowDto,
  CollectionWorkflowResponse,
  CommitTargetFileSetRequest,
  StageTargetFileResponse,
  ValidateTargetFilesRequest,
  VersionedCollectionRevision,
  VersionedCollectionRevisionFileSetCommit,
  VersionedCollectionRevisionTargetFileSet,
} from '../interfaces/collection-revision.interface';
import { withCollectionRequestContext } from '../interceptors/collection-request-context';
import {
  buildCollectionCurrentWorkflowUrl,
  buildCollectionExactWorkflowUrl,
  buildCollectionRevisionTargetUrl,
  buildCollectionRevisionUrl,
} from './collection-revision-urls';

const REVISION_ETAG_HEADER = 'X-Collection-Revision-ETag';
const TARGET_ETAG_HEADER = 'ETag';

export class CollectionRevisionWireContractError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'CollectionRevisionWireContractError';
  }
}

export class CollectionRevisionPreconditionConflict extends Error {
  constructor(
    readonly current: CollectionRevisionCurrentDto,
    readonly targetEtag: string,
    readonly revisionEtag: string,
    readonly traceId: string | null,
  ) {
    super('The collection revision target changed remotely.');
    this.name = 'CollectionRevisionPreconditionConflict';
  }
}

export class CollectionRevisionLifecyclePreconditionConflict extends Error {
  constructor(
    readonly current: CollectionRevisionDto,
    readonly revisionEtag: string,
    readonly traceId: string | null,
  ) {
    super('The collection revision changed remotely.');
    this.name = 'CollectionRevisionLifecyclePreconditionConflict';
  }
}

interface IntegrationAuthorizationWireResponse {
  readonly integration_redirect: {
    readonly should_redirect: boolean;
    readonly url: string;
    readonly result_status: string;
    readonly expires_at?: string | null;
  };
}

@Injectable({ providedIn: 'root' })
export class CollectionRevisionService {
  private readonly http = inject(HttpClient);
  private readonly idempotency = inject(CollectionRequestIdempotencyService);
  private readonly apiRoot = `${environment.apiUrl}/api/chordium/v1`;
  private readonly fileApiRoot = `${environment.apiUrlLegacy}/api/authorization/chordium/v1`;

  getWorkflow(collectionId: string): Observable<CollectionWorkflowDto> {
    return this.getCurrentWorkflow(collectionId);
  }

  getCurrentWorkflow(collectionId: string): Observable<CollectionWorkflowDto> {
    const exactCollectionId = requirePlainId(collectionId, 'collectionId');
    return this.http
      .get<CollectionWorkflowResponse>(
        buildCollectionCurrentWorkflowUrl(this.apiRoot, exactCollectionId),
        {
          context: withCollectionRequestContext(exactCollectionId),
          observe: 'response',
        },
      )
      .pipe(
        map((response) =>
          toWorkflow(response, { collectionId: exactCollectionId }),
        ),
      );
  }

  getExactWorkflow(
    collectionId: string,
    revisionId: string,
  ): Observable<CollectionWorkflowDto> {
    return this.http
      .get<CollectionWorkflowResponse>(
        buildCollectionExactWorkflowUrl(this.apiRoot, collectionId, revisionId),
        {
          context: withCollectionRequestContext(collectionId),
          observe: 'response',
        },
      )
      .pipe(
        map((response) =>
          toWorkflow(response, {
            collectionId: requirePlainId(collectionId, 'collectionId'),
            revisionId: requirePlainId(revisionId, 'revisionId'),
          }),
        ),
      );
  }

  getRevision(
    collectionId: string,
    revisionId: string,
  ): Observable<VersionedCollectionRevision> {
    return this.http
      .get<CollectionRevisionResponse>(
        buildCollectionRevisionUrl(this.apiRoot, collectionId, revisionId),
        {
          context: withCollectionRequestContext(collectionId),
          observe: 'response',
        },
      )
      .pipe(
        map((response) => ({
          revision: requireBody(response).revision,
          revisionEtag: requireHeader(response, REVISION_ETAG_HEADER),
        })),
      );
  }

  sealRevision(
    collectionId: string,
    revisionId: string,
    revisionEtag: string,
  ): Observable<VersionedCollectionRevision> {
    return this.http
      .post<CollectionRevisionResponse>(
        `${buildCollectionRevisionUrl(
          this.apiRoot,
          collectionId,
          revisionId,
        )}/seal`,
        null,
        {
          context: withCollectionRequestContext(collectionId),
          headers: new HttpHeaders({ 'If-Match': requireEtag(revisionEtag) }),
          observe: 'response',
        },
      )
      .pipe(
        map((response) => ({
          revision: requireBody(response).revision,
          revisionEtag: requireHeader(response, REVISION_ETAG_HEADER),
        })),
        catchError((error: unknown) =>
          throwError(() => mapLifecycleError(error)),
        ),
      );
  }

  authorizeIntegrationRevision(
    collectionId: string,
    revisionId: string,
  ): Observable<CollectionIntegrationAuthorization> {
    return this.http
      .post<IntegrationAuthorizationWireResponse>(
        `${buildCollectionRevisionUrl(
          this.apiRoot,
          collectionId,
          revisionId,
        )}/integration/authorization`,
        null,
        {
          context: withCollectionRequestContext(collectionId),
          observe: 'response',
        },
      )
      .pipe(map(toIntegrationAuthorization));
  }

  getTargetFileSet(
    collectionId: string,
    revisionId: string,
    targetId: string,
  ): Observable<VersionedCollectionRevisionTargetFileSet> {
    return this.http
      .get<CollectionRevisionTargetFileSetResponse>(
        `${buildCollectionRevisionTargetUrl(
          this.apiRoot,
          collectionId,
          revisionId,
          targetId,
        )}/files`,
        {
          context: withCollectionRequestContext(collectionId),
          observe: 'response',
        },
      )
      .pipe(map(toVersionedTargetFileSet));
  }

  stageTargetFile(
    collectionId: string,
    revisionId: string,
    targetId: string,
    file: File,
  ): Observable<StageTargetFileResponse> {
    const body = new FormData();
    body.append('file', file);
    return this.http.post<StageTargetFileResponse>(
      `${buildCollectionRevisionTargetUrl(
        this.fileApiRoot,
        collectionId,
        revisionId,
        targetId,
      )}/files/stage`,
      body,
      { context: withCollectionRequestContext(collectionId) },
    );
  }

  validateTargetFiles(
    collectionId: string,
    revisionId: string,
    targetId: string,
    request: ValidateTargetFilesRequest,
  ): Observable<CollectionRevisionValidationResponse> {
    return this.http.post<CollectionRevisionValidationResponse>(
      `${buildCollectionRevisionTargetUrl(
        this.fileApiRoot,
        collectionId,
        revisionId,
        targetId,
      )}/files/validate`,
      cloneFileSetRequest(request),
      { context: withCollectionRequestContext(collectionId) },
    );
  }

  discardTargetFile(
    collectionId: string,
    revisionId: string,
    targetId: string,
    fileId: string,
  ): Observable<void> {
    return this.http.delete<void>(
      `${buildCollectionRevisionTargetUrl(
        this.fileApiRoot,
        collectionId,
        revisionId,
        targetId,
      )}/files/${encodeRequiredId(fileId, 'fileId')}`,
      { context: withCollectionRequestContext(collectionId) },
    );
  }

  commitTargetFileSet(
    collectionId: string,
    revisionId: string,
    targetId: string,
    targetEtag: string,
    request: CommitTargetFileSetRequest,
  ): Observable<VersionedCollectionRevisionFileSetCommit> {
    const exactRequest = cloneFileSetRequest(request);
    const operation: CommitTargetFileSetIdempotentOperation = {
      kind: 'commit-target-file-set',
      collectionId,
      revisionId,
      targetId,
      targetEtag: requireEtag(targetEtag),
      fileInputs: exactRequest.files,
    };
    const context = withCollectionRequestContext(collectionId).set(
      COLLECTION_IDEMPOTENCY_OPERATION,
      operation,
    );

    return this.http
      .post<CollectionRevisionFileSetCommitResponse>(
        `${buildCollectionRevisionTargetUrl(
          this.apiRoot,
          collectionId,
          revisionId,
          targetId,
        )}/file-set-commits`,
        exactRequest,
        {
          context,
          headers: new HttpHeaders({ 'If-Match': operation.targetEtag }),
          observe: 'response',
        },
      )
      .pipe(
        map((response) => ({
          commit: requireBody(response).file_set_commit,
          targetEtag: requireHeader(response, TARGET_ETAG_HEADER),
          revisionEtag: requireHeader(response, REVISION_ETAG_HEADER),
        })),
        tap(() => this.idempotency.clear(operation)),
        catchError((error: unknown) => {
          if (isDefinitiveFailure(error)) this.idempotency.clear(operation);
          return throwError(() => mapCommitError(error));
        }),
      );
  }
}

function toWorkflow(
  response: HttpResponse<CollectionWorkflowResponse>,
  expected: { readonly collectionId: string; readonly revisionId?: string },
): CollectionWorkflowDto {
  const body: unknown = requireBody(response);
  if (
    !isRecord(body) ||
    !hasExactKeys(body, ['workflow']) ||
    !isWorkflow(body['workflow'])
  ) {
    throw new CollectionRevisionWireContractError(
      'Nested workflow response is required.',
    );
  }
  const workflow = body['workflow'];
  if (
    workflow.collection_id !== expected.collectionId ||
    (expected.revisionId !== undefined &&
      workflow.revision_id !== expected.revisionId)
  ) {
    throw new CollectionRevisionWireContractError(
      'Exact workflow identity does not match the requested route.',
    );
  }
  const etag = requireHeader(response, TARGET_ETAG_HEADER);
  const revisionEtag = requireHeader(response, REVISION_ETAG_HEADER);
  if (etag !== workflow.etag || revisionEtag !== workflow.etag) {
    throw new CollectionRevisionWireContractError(
      'Workflow validators do not match its body.',
    );
  }
  return workflow;
}

function toIntegrationAuthorization(
  response: HttpResponse<IntegrationAuthorizationWireResponse>,
): CollectionIntegrationAuthorization {
  if (response.headers.get('Cache-Control') !== 'no-store') {
    throw new CollectionRevisionWireContractError(
      'Integration authorization must be returned with Cache-Control: no-store.',
    );
  }
  const body: unknown = requireBody(response);
  if (!isRecord(body) || !isRecord(body['integration_redirect'])) {
    throw new CollectionRevisionWireContractError(
      'Integration authorization response is invalid.',
    );
  }
  const redirect = body['integration_redirect'];
  const shouldRedirect = redirect['should_redirect'];
  const url = nonEmptyString(redirect['url']);
  const resultStatus = nonEmptyString(redirect['result_status']);
  const expiresAt = redirect['expires_at'];
  if (
    typeof shouldRedirect !== 'boolean' ||
    !url ||
    !resultStatus ||
    (expiresAt !== undefined &&
      expiresAt !== null &&
      typeof expiresAt !== 'string')
  ) {
    throw new CollectionRevisionWireContractError(
      'Integration authorization redirect is invalid.',
    );
  }
  return Object.freeze({
    shouldRedirect,
    url,
    resultStatus,
    expiresAt: typeof expiresAt === 'string' ? expiresAt : null,
  });
}

function toVersionedTargetFileSet(
  response: HttpResponse<CollectionRevisionTargetFileSetResponse>,
): VersionedCollectionRevisionTargetFileSet {
  return {
    targetFileSet: requireBody(response).target_file_set,
    targetEtag: requireHeader(response, TARGET_ETAG_HEADER),
    revisionEtag: requireHeader(response, REVISION_ETAG_HEADER),
  };
}

function requireBody<T>(response: HttpResponse<T>): T {
  if (response.body === null) {
    throw new CollectionRevisionWireContractError('Response body is required.');
  }
  return response.body;
}

function requireHeader<T>(response: HttpResponse<T>, name: string): string {
  const value = response.headers.get(name);
  if (!value) {
    throw new CollectionRevisionWireContractError(
      `Response header ${name} is required.`,
    );
  }
  return value;
}

function cloneFileSetRequest(
  request: CommitTargetFileSetRequest | ValidateTargetFilesRequest,
): {
  files: {
    file_id: string;
    resolved_inputs: Record<string, string>;
  }[];
} {
  const seen = new Set<string>();
  return {
    files: request.files.map((file) => {
      const fileId = requirePlainId(file.file_id, 'file_id');
      if (seen.has(fileId)) {
        throw new TypeError('files must contain unique file ids');
      }
      seen.add(fileId);
      return {
        file_id: fileId,
        resolved_inputs: cloneResolvedInputs(file.resolved_inputs),
      };
    }),
  };
}

function cloneResolvedInputs(
  inputs: Readonly<Record<string, string>>,
): Record<string, string> {
  if (!isRecord(inputs)) {
    throw new TypeError('resolved_inputs must be an object');
  }
  return Object.fromEntries(
    Object.entries(inputs)
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([key, value]) => {
        const normalizedKey = requirePlainId(key, 'resolved_inputs key');
        if (typeof value !== 'string' || value.trim().length === 0) {
          throw new TypeError(
            'resolved_inputs values must be non-empty strings',
          );
        }
        return [normalizedKey, value.trim()];
      }),
  );
}

function mapCommitError(error: unknown): unknown {
  if (!(error instanceof HttpErrorResponse) || error.status !== 412)
    return error;
  if (!isRecord(error.error) || !isRecord(error.error['error'])) {
    return new CollectionRevisionWireContractError(
      'Precondition response envelope is invalid.',
    );
  }
  const details = error.error['error']['details'];
  if (!isRecord(details) || !isCurrentDto(details['current'])) {
    return new CollectionRevisionWireContractError(
      'Precondition current representation is required.',
    );
  }
  const targetEtag = nonEmptyString(details['current_etag']);
  const revisionEtag = nonEmptyString(details['current_revision_etag']);
  const headerTargetEtag = error.headers.get(TARGET_ETAG_HEADER);
  const headerRevisionEtag = error.headers.get(REVISION_ETAG_HEADER);
  if (
    !targetEtag ||
    !revisionEtag ||
    headerTargetEtag !== targetEtag ||
    headerRevisionEtag !== revisionEtag
  ) {
    return new CollectionRevisionWireContractError(
      'Precondition response validators do not match its body.',
    );
  }
  return new CollectionRevisionPreconditionConflict(
    details['current'],
    targetEtag,
    revisionEtag,
    nonEmptyString(error.error['trace_id']),
  );
}

function mapLifecycleError(error: unknown): unknown {
  if (!(error instanceof HttpErrorResponse) || error.status !== 412) {
    return error;
  }
  if (!isRecord(error.error) || !isRecord(error.error['error'])) {
    return new CollectionRevisionWireContractError(
      'Lifecycle precondition response envelope is invalid.',
    );
  }
  const details = error.error['error']['details'];
  if (
    !isRecord(details) ||
    !isRecord(details['current']) ||
    !isRevisionDto(details['current']['revision'])
  ) {
    return new CollectionRevisionWireContractError(
      'Lifecycle precondition current revision is required.',
    );
  }
  const revisionEtag = nonEmptyString(details['current_revision_etag']);
  if (
    !revisionEtag ||
    error.headers.get(REVISION_ETAG_HEADER) !== revisionEtag
  ) {
    return new CollectionRevisionWireContractError(
      'Lifecycle precondition validator does not match its body.',
    );
  }
  return new CollectionRevisionLifecyclePreconditionConflict(
    details['current']['revision'],
    revisionEtag,
    nonEmptyString(error.error['trace_id']),
  );
}

function isCurrentDto(value: unknown): value is CollectionRevisionCurrentDto {
  return (
    isRecord(value) && 'revision' in value && isRecord(value['target_file_set'])
  );
}

function isRevisionDto(value: unknown): value is CollectionRevisionDto {
  return (
    isRecord(value) &&
    nonEmptyString(value['id']) !== null &&
    nonEmptyString(value['collection_id']) !== null &&
    typeof value['revision_no'] === 'number' &&
    (value['kind'] === 'INITIAL' ||
      value['kind'] === 'MANUAL_CORRECTION' ||
      value['kind'] === 'RECURRING_SYNC') &&
    (value['status'] === 'DRAFT' ||
      value['status'] === 'SEALED' ||
      value['status'] === 'SUBMITTED' ||
      value['status'] === 'CANCELLED') &&
    typeof value['editable'] === 'boolean' &&
    Array.isArray(value['allowed_actions'])
  );
}

function isWorkflow(value: unknown): value is CollectionWorkflowDto {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, WORKFLOW_KEYS) ||
    nonEmptyString(value['collection_id']) === null ||
    nonEmptyString(value['revision_id']) === null ||
    !Number.isInteger(value['revision_no']) ||
    (value['revision_no'] as number) < 1 ||
    (value['revision_kind'] !== 'INITIAL' &&
      value['revision_kind'] !== 'MANUAL_CORRECTION') ||
    !isRevisionStatus(value['revision_status']) ||
    typeof value['editable'] !== 'boolean' ||
    !isStringArray(value['allowed_actions']) ||
    nonEmptyString(value['etag']) === null ||
    !Array.isArray(value['targets']) ||
    !value['targets'].every(isWorkflowTarget)
  ) {
    return false;
  }
  const targetIds = new Set<string>();
  const orderIndexes = new Set<number>();
  for (const target of value['targets']) {
    const targetId = target['target_id'] as string;
    const orderIndex = target['order_idx_snapshot'] as number;
    if (targetIds.has(targetId) || orderIndexes.has(orderIndex)) return false;
    targetIds.add(targetId);
    orderIndexes.add(orderIndex);
  }
  return true;
}

const WORKFLOW_KEYS = [
  'collection_id',
  'revision_id',
  'revision_no',
  'revision_kind',
  'revision_status',
  'editable',
  'allowed_actions',
  'etag',
  'targets',
] as const;

const WORKFLOW_TARGET_KEYS = [
  'target_id',
  'order_idx_snapshot',
  'revenue_type',
  'revenue_subtype',
  'mean_type_snapshot',
  'target_status',
  'acquisition_outcome',
] as const;

function isWorkflowTarget(value: unknown): value is Record<string, unknown> {
  return (
    isRecord(value) &&
    hasExactKeys(value, WORKFLOW_TARGET_KEYS) &&
    nonEmptyString(value['target_id']) !== null &&
    Number.isInteger(value['order_idx_snapshot']) &&
    (value['order_idx_snapshot'] as number) >= 0 &&
    nonEmptyString(value['revenue_type']) !== null &&
    isNullableNonEmptyString(value['revenue_subtype']) &&
    (value['mean_type_snapshot'] === 'CONNECTION' ||
      value['mean_type_snapshot'] === 'UPLOAD') &&
    (value['target_status'] === 'PENDING' ||
      value['target_status'] === 'IN_PROGRESS' ||
      value['target_status'] === 'COMPLETED') &&
    isNullableNonEmptyString(value['acquisition_outcome'])
  );
}

function isRevisionStatus(value: unknown): boolean {
  return (
    value === 'DRAFT' ||
    value === 'SEALED' ||
    value === 'SUBMITTED' ||
    value === 'CANCELLED'
  );
}

function isStringArray(value: unknown): value is readonly string[] {
  return (
    Array.isArray(value) &&
    value.every((item) => nonEmptyString(item) !== null) &&
    new Set(value).size === value.length
  );
}

function isNullableNonEmptyString(value: unknown): boolean {
  return value === null || nonEmptyString(value) !== null;
}

function hasExactKeys(
  value: Record<string, unknown>,
  expected: readonly string[],
): boolean {
  const keys = Object.keys(value);
  const expectedSet = new Set(expected);
  return (
    keys.length === expected.length && keys.every((key) => expectedSet.has(key))
  );
}

function isDefinitiveFailure(error: unknown): boolean {
  if (!(error instanceof HttpErrorResponse)) return true;
  return !(error.status === 0 || error.status === 408 || error.status >= 500);
}

function requireEtag(value: string): string {
  if (typeof value !== 'string' || value.trim().length === 0) {
    throw new TypeError('targetEtag must be a non-empty string');
  }
  return value;
}

function encodeRequiredId(value: string, field: string): string {
  return encodeURIComponent(requirePlainId(value, field));
}

function requirePlainId(value: string, field: string): string {
  if (typeof value !== 'string' || value.trim().length === 0) {
    throw new TypeError(`${field} must be a non-empty string`);
  }
  return value.trim();
}

function nonEmptyString(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
